import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect } from "react";
import { useImmer } from "use-immer";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import {
  isShopifyPickQuestion,
  Option,
} from "@intelligems/ig-types/schemas/ai/nodes/input-requirement";
import { SURVEY_DECLINED_MESSAGE } from "@intelligems/ig-types/schemas/ai/survey-reply";
import { UserInputAnswer } from "@intelligems/ig-types/schemas/ai/node-execution";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material";
import { useOptionalPromptInputAttachments } from "@src/components/ai-elements/prompt-input-context/useOptionalPromptInputAttachments";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { ShopifyEntityCombobox } from "@src/features/ai/components/WorkflowEditor/execution/panel/ShopifyEntityCombobox";
import {
  ProductPickerFilters,
  productPickSeedFilters,
  toProductPickerFilters,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/productPickSeed";
import { ShopifyProductPickerDialog } from "@src/features/ai/components/WorkflowEditor/execution/panel/ShopifyProductPickerDialog";
import {
  shopifyEntityChooseLabel,
  ShopifyEntityPick,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/shopifyEntityPickerCopy";
import {
  deriveSurveyQuestions,
  surveyCardTitle,
  surveyOptionRows,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/surveyAskState";
import {
  SurveyCardShell,
  SurveyCardStatus,
  SurveyCheckboxOption,
  SurveyNumberedOption,
  SurveyOptionList,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/SurveyCardParts";
import { useIsTextRevealing } from "@src/features/ai/components/WorkflowEditor/execution/turn/AssistantMessage/useIsTextRevealing";
import { IgCoreTextColors } from "@intelligems/ig-design-tokens/constants";

/**
 * The Figma "Survey card" (Atlas Component Library › Survey cards): how a
 * parked `request-user-input` question renders above the composer. One card
 * for every card-shaped ask — a legacy `choice` or `confirmation` is a single
 * question (no stepper, just the close X); a `survey` steps through its
 * questions with `‹ N of M ›` in the header. The chrome and rows are the
 * shared `SurveyCardParts`, which the panel's own cart offer uses as well.
 *
 * A `file` ask (Survey card › Select=File, 472:302) is the same shell with no
 * option rows: the footer holds one full-width outlined "Choose file" button
 * that opens the composer's file dialog. Once a file is picked (478:318) the
 * card owns it — the body lists every attachment as a removable chip and the
 * footer button becomes "Send to chat", which submits the composer's draft
 * plus the uploaded files (the panel stays disabled while an upload is still
 * settling, so the button only sends a file that is actually ready). The X
 * declines exactly as for a question.
 *
 * The question ALWAYS renders in the header — it is the card's title — but
 * only up to about three lines: a longer prompt is replaced by
 * `SURVEY_CARD_TITLE_FALLBACK`, which points at the transcript bubble that
 * shows the full text in that one case (see `surveyCardTitle`). Otherwise the
 * transcript does not repeat the question at all.
 *
 * Option rows follow `surveyOptionRows`: the default option says "(default)"
 * in its title, and sublines are all-or-none within the card.
 *
 * A `shopify-pick` question renders its catalog inline (Figma "Atlas / Chat
 * combobox"): for collections, tags, vendors and product types the card body
 * IS the picker — a searchable, scrollable run of rows with Confirm selections
 * in the footer (see `ShopifyEntityCombobox`), and a multi-select one counts
 * "N selected" beside the X once something is picked. Products keep the
 * Survey card › Select=Picker shape, since the shared product picker's images,
 * variants and filters have no inline equivalent: the footer button opens that
 * dialog, and once answered the body lists what was picked and the button
 * reopens it. Either way the answer carries ids for the agent and labels for
 * the transcript.
 *
 * Answering a single-select question is one click: the first click on a row
 * is the answer and the card auto-advances (or submits, on the last unanswered
 * question). Only a `multiSelect` question gets a Confirm selections button:
 * rows toggle, nothing sends until confirmation, and the reply lists every
 * picked option.
 *
 * Stepper rules (per the Figma spec): back (‹) shows on every question except
 * the first; forward (›) only once the next question is already answered —
 * never on the last — so nobody skips ahead. Hidden arrows keep their slot
 * reserved so the header never shifts. The X dismisses the card and resumes
 * the agent with a declined-to-answer message. Nothing renders while an
 * assistant bubble is still revealing itself, so the answer is never on offer
 * before the question has been shown.
 */
export const SurveyCard = () => {
  const theme = useTheme();
  const { inputRequirement, submitInput, onSubmit, survey, disabled } =
    useExecutionPanelContext();
  // The card answers the message that is still being painted; offering it
  // first lets a merchant answer a question they have not been shown.
  const revealing = useIsTextRevealing();

  const attachments = useOptionalPromptInputAttachments();

  const questions = deriveSurveyQuestions(inputRequirement);
  const currentIndex = survey?.currentIndex ?? 0;
  const question = questions?.[currentIndex];
  const existingAnswer = survey?.answers[currentIndex];

  // Multi-select assembly state for the CURRENT question — option values for
  // an options question, catalog picks for a combobox. Re-seeded per question
  // (and per requirement) from any answer already recorded, so stepping back
  // shows the set that was picked — and a new question never inherits the
  // previous one's toggles.
  const [freeText, setFreeText] = useImmer("");
  const [selected, setSelected] = useImmer<string[]>([]);
  const [picked, setPicked] = useImmer<ShopifyEntityPick[]>([]);
  useEffect(() => {
    setFreeText(() => existingAnswer?.freeText ?? "");
    setSelected(
      () => existingAnswer?.choices.map((choice) => choice.value) ?? []
    );
    setPicked(
      () =>
        existingAnswer?.choices.map((choice) => ({
          id: choice.value,
          label: choice.label,
        })) ?? []
    );
  }, [
    inputRequirement,
    currentIndex,
    existingAnswer,
    setSelected,
    setPicked,
    setFreeText,
  ]);
  // Whether the current pick question's store picker is open. Closed again
  // whenever the question changes, so stepping away never leaves a dialog for
  // a question that is no longer on the card.
  const [pickerOpen, setPickerOpen] = useImmer(false);
  useEffect(() => {
    setPickerOpen(() => false);
  }, [inputRequirement, currentIndex, setPickerOpen]);
  // Per product-pick step: the picker filters the merchant left it with, when
  // they changed the seed. Kept across steps so going back to change an
  // earlier answer re-seeds the pick only if the merchant never edited its
  // filters; a new requirement starts clean.
  const [editedPickerFilters, setEditedPickerFilters] = useImmer<
    Record<number, ProductPickerFilters>
  >({});
  useEffect(() => {
    setEditedPickerFilters(() => ({}));
  }, [inputRequirement, setEditedPickerFilters]);

  if (revealing) {
    return null;
  }

  const declineAsk = () =>
    submitInput({ type: "text", content: SURVEY_DECLINED_MESSAGE });

  if (inputRequirement.userInput.type === "file") {
    const files = attachments?.files ?? [];
    return (
      <SurveyCardShell
        title={surveyCardTitle(inputRequirement.prompt ?? "")}
        disabled={disabled}
        hasFooter
        onDismiss={declineAsk}
      >
        {files.length > 0 && (
          <Stack
            data-testid="survey-card-files"
            direction="row"
            flexWrap="wrap"
            gap={2}
            sx={{ px: 3, py: 2 }}
          >
            {files.map((file) => {
              const filename = file.filename ?? "(unnamed)";
              return (
                // Figma "file-chip": Background/Interactive/Neutral/Default,
                // 6px radius, 8px before the name and 4px around the X.
                <Stack
                  key={file.id}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{
                    backgroundColor:
                      theme.igBackgroundColors.interactive.neutral.default,
                    borderRadius: "6px",
                    maxWidth: "100%",
                    pl: 2,
                    pr: 1,
                    py: 1,
                  }}
                >
                  <IgTypography
                    variant="body2"
                    noWrap
                    sx={{ color: IgCoreTextColors.primary, lineHeight: "18px" }}
                  >
                    {filename}
                  </IgTypography>
                  <IgIconButton
                    aria-label={`Remove ${filename}`}
                    size="small"
                    disabled={disabled}
                    onClick={() => attachments?.remove(file.id)}
                    sx={{ p: 0 }}
                  >
                    <X size={16} color={theme.igIconColors.light} />
                  </IgIconButton>
                </Stack>
              );
            })}
          </Stack>
        )}
        <Stack sx={{ px: 3, pb: 3, pt: 2 }}>
          <IgButton
            fullWidth
            variant="outlined"
            disabled={disabled || !attachments}
            onClick={
              files.length > 0 ? onSubmit : () => attachments?.openFileDialog()
            }
            sx={{ height: 36 }}
          >
            {files.length > 0 ? "Send to chat" : "Choose file"}
          </IgButton>
        </Stack>
      </SurveyCardShell>
    );
  }

  if (!questions || !question) {
    return null;
  }

  const total = questions.length;
  const multiSelect = question.multiSelect === true;
  const showStepper = total > 1 && survey !== null;
  const showBack = currentIndex > 0;
  const showForward =
    currentIndex < total - 1 && Boolean(survey?.answers[currentIndex + 1]);

  // Stepping back shows the row that was picked as pressed.
  const answeredValues = new Set(
    existingAnswer?.choices.map((choice) => choice.value) ?? []
  );

  // One answer path for every question kind: record it on the stepper when
  // there is one, else submit it as the single-question reply.
  const answer = (choices: UserInputAnswer["choices"], freeText?: string) => {
    const response: UserInputAnswer = {
      prompt: question.prompt,
      choices,
      ...(freeText !== undefined ? { freeText } : {}),
    };
    if (survey) {
      survey.answerCurrent(response);
      return;
    }
    submitInput({
      type: "answers",
      answers: [response],
    });
  };

  const customAnswer =
    question.allowFreeText === true ? (
      <Stack
        component="form"
        direction="row"
        gap={2}
        sx={{ px: 3, pb: 3, pt: 2 }}
        onSubmit={(event) => {
          event.preventDefault();
          if (!disabled && freeText.trim()) {
            answer([], freeText.trim());
          }
        }}
      >
        <IgTextField
          fullWidth
          disabled={disabled}
          placeholder="Or type your own answer"
          value={freeText}
          onChange={(event) => setFreeText(event.target.value)}
          slotProps={{ htmlInput: { "aria-label": "Custom answer" } }}
        />
        <IgButton
          type="submit"
          variant="outlined"
          disabled={disabled || !freeText.trim()}
          sx={{ flexShrink: 0 }}
        >
          Submit answer
        </IgButton>
      </Stack>
    ) : null;

  const submitSingle = (option: Option) =>
    answer([{ value: option.value, label: option.label }]);

  const submitSelection = () => {
    if (isShopifyPickQuestion(question)) {
      return;
    }
    // Keep the agent's own option order so two users picking the same set send
    // the same reply.
    const chosen = question.options
      .filter((option) => selected.includes(option.value))
      .map((option) => ({ value: option.value, label: option.label }));
    answer(chosen, chosen.length === 0 ? "None" : undefined);
  };

  const submitPick = (items: ShopifyEntityPick[]) => {
    setPickerOpen(() => false);
    answer(
      items.map((item) => ({ value: item.id, label: item.label })),
      items.length === 0 ? "None" : undefined
    );
  };

  const toggleOption = (value: string) =>
    setSelected((current) =>
      current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value]
    );

  const dismiss = () => {
    if (survey) {
      survey.dismiss();
      return;
    }
    declineAsk();
  };

  const stepper = showStepper ? (
    <Stack direction="row" alignItems="center" gap={1}>
      {/* Hidden arrows stay in the layout so the stepper's width never
          changes and the header doesn't shift as they come and go. */}
      <IgIconButton
        aria-label="Previous question"
        size="small"
        disabled={disabled}
        onClick={() => survey.goTo(currentIndex - 1)}
        sx={{ p: 0, visibility: showBack ? "visible" : "hidden" }}
      >
        <ChevronLeft size={16} color={theme.igIconColors.light} />
      </IgIconButton>
      <SurveyCardStatus>
        {currentIndex + 1} of {total}
      </SurveyCardStatus>
      <IgIconButton
        aria-label="Next question"
        size="small"
        disabled={disabled}
        onClick={() => survey.goTo(currentIndex + 1)}
        sx={{ p: 0, visibility: showForward ? "visible" : "hidden" }}
      >
        <ChevronRight size={16} color={theme.igIconColors.light} />
      </IgIconButton>
    </Stack>
  ) : null;

  if (isShopifyPickQuestion(question)) {
    // Every flat catalog kind is picked inline, in the card, from the Figma
    // chat combobox. Only products keep a dialog: the shared picker's images,
    // variants and filters have no inline equivalent.
    if (question.entity !== "product") {
      return (
        <SurveyCardShell
          title={surveyCardTitle(question.prompt)}
          stepper={stepper}
          disabled={disabled}
          hasFooter
          tone="subtle"
          onDismiss={dismiss}
        >
          <ShopifyEntityCombobox
            // Remounted per question so its search starts empty for each one.
            key={currentIndex}
            entity={question.entity}
            multiSelect={multiSelect}
            disabled={disabled}
            selected={picked}
            onSelectedChange={(next) => setPicked(() => next)}
            onConfirm={submitPick}
            onNone={() => answer([], "None")}
          />
          {customAnswer}
        </SurveyCardShell>
      );
    }

    const answeredPicks = existingAnswer?.choices ?? [];
    return (
      <SurveyCardShell
        title={surveyCardTitle(question.prompt)}
        stepper={stepper}
        disabled={disabled}
        hasFooter
        onDismiss={dismiss}
      >
        {answeredPicks.length > 0 && (
          <Stack
            component="ul"
            data-testid="survey-card-picked"
            sx={{ listStyle: "none", m: 0, px: 3, py: 0 }}
          >
            {answeredPicks.map((item) => (
              <IgTypography
                key={item.value}
                component="li"
                variant="body2"
                noWrap
                sx={{ py: 1 }}
              >
                {item.label}
              </IgTypography>
            ))}
          </Stack>
        )}
        <Stack direction="row" gap={2} sx={{ px: 3, pb: 3, pt: 2 }}>
          <IgButton
            variant="outlined"
            disabled={disabled}
            onClick={() => answer([], "None")}
            sx={{ height: 36 }}
          >
            Skip
          </IgButton>
          <IgButton
            fullWidth
            variant="outlined"
            disabled={disabled}
            onClick={() => setPickerOpen(() => true)}
            sx={{ height: 36 }}
          >
            {answeredPicks.length > 0
              ? "Change selection"
              : shopifyEntityChooseLabel(question.entity, multiSelect)}
          </IgButton>
        </Stack>
        {customAnswer}
        <ShopifyProductPickerDialog
          multiSelect={multiSelect}
          seedFilters={toProductPickerFilters(
            productPickSeedFilters(question, survey?.answers ?? {})
          )}
          editedFilters={editedPickerFilters[currentIndex]}
          excludedProductIds={question.exclude?.product_ids ?? []}
          onFiltersLeft={(edited) =>
            setEditedPickerFilters((draft) => {
              if (edited) {
                draft[currentIndex] = edited;
              } else {
                delete draft[currentIndex];
              }
            })
          }
          open={pickerOpen}
          saveDisabled={disabled}
          onClose={() => setPickerOpen(() => false)}
          onSave={submitPick}
        />
      </SurveyCardShell>
    );
  }

  const rows = surveyOptionRows(question.options);

  return (
    <SurveyCardShell
      title={surveyCardTitle(question.prompt)}
      stepper={stepper}
      disabled={disabled}
      hasFooter={multiSelect || question.allowFreeText === true}
      onDismiss={dismiss}
    >
      <SurveyOptionList>
        {rows.map((row, index) =>
          multiSelect ? (
            <SurveyCheckboxOption
              key={row.option.value}
              title={row.title}
              subline={row.subline}
              checked={selected.includes(row.option.value)}
              disabled={disabled}
              onToggle={() => toggleOption(row.option.value)}
            />
          ) : (
            <SurveyNumberedOption
              key={row.option.value}
              index={index}
              title={row.title}
              subline={row.subline}
              pressed={answeredValues.has(row.option.value)}
              disabled={disabled}
              onClick={() => submitSingle(row.option)}
            />
          )
        )}
      </SurveyOptionList>

      {customAnswer}
      {multiSelect && (
        <Stack sx={{ px: 3, pb: 3, pt: 2 }}>
          <IgButton
            fullWidth
            variant="outlined"
            disabled={disabled}
            onClick={submitSelection}
            sx={{ height: 36 }}
          >
            Confirm selections
          </IgButton>
        </Stack>
      )}
    </SurveyCardShell>
  );
};
